//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { retry } from 'rxjs';

//Local libs
import Service from '../common/service';
import CreditsManagersModel from './creditsManagers.model';

/**
 * @service CreditsManagersService
 * @description Service for managing subscriber credit data and subscription details.
 * Provides HTTP methods for retrieving credit management information for business entities.
 * Handles subscription data retrieval with automatic retry logic for resilience.
 * Extends base Service class with generic type CreditsManagersModel.
 *
 * @feature 1. Fetch subscriptions by business ID with related business type data
 * @feature 2. Automatic retry mechanism (2 retries) for network resilience
 * @feature 3. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 4. Promise-based async operations for subscription management
 *
 * @architecture
 * Extends base Service<CreditsManagersModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling and RxJS operators.
 *
 * @service-endpoint /tradingcommunity/creditsmanager/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CreditsManagersService extends Service<CreditsManagersModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/creditsmanager/');
  }

  /**
   * @method getSubscriptionsByBusinessID
   * @async
   * @description Fetch subscription details for business entities
   *
   * Makes HTTP GET request to backend endpoint /tradingcommunity/creditsmanager/
   * with query parameters to include business type details. Implements retry logic
   * to attempt the request up to 2 times on failure.
   *
   * @returns {Promise<Array<CreditsManagersModel>>} Promise resolving to array of CreditsManagersModel objects
   *
   * @query-params
   * - include: businessID>businessType
   * - pageSize: 5000
   * - order: new
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails after retries
   * @example
   * const subscriptions = await creditsManagersService.getSubscriptionsByBusinessID();
   * console.log(subscriptions); // Array of subscription records
   */
  public getSubscriptionsByBusinessID(): Promise<Array<CreditsManagersModel>> {
    return new Promise<Array<CreditsManagersModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<CreditsManagersModel>>(
          `${this.baseUrl}${this.serviceUrl}?include=businessID>businessType&pageSize=5000&order=new`,
          this.httpOptions
        )
        .pipe(
          retry(2) // retry up to 2 times
        )
        .subscribe(
          (response) => {
            resolve(response);
          },
          (error: HttpErrorResponse) => {
            reject(error);
          }
        );

      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.creditsManagersService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
